import type { FC } from 'react';
import React from 'react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { B200, R200 } from '@atlaskit/theme/colors';

import type { ContentStateRestrictionLevel } from '@confluence/content-state/entry-points/EditorContentStateQuery';

import { PRIMARY_COLORS } from './colors';
import { hexToToken } from './helpers';

export type ColorAppearance =
	| 'pageHistoryContentState'
	| 'rendererContentState'
	| 'editorContentState'
	| 'contentStateButton'
	| 'restoreContentStatesModal'
	| 'spaceAdminContentState'
	| 'subtleContentStateButton'
	| 'subtleRendererContentState';
type Appearance = {
	ColorContainerStyles: ReturnType<typeof xcss>;
	PlainContentStateColorStyles: ReturnType<typeof xcss>;
	// Props for the SVG
	scale?: string;
	viewbox?: string;
};

type AppearanceMap = {
	[key in ColorAppearance]: Appearance;
};

const ContentStateColorSizes: AppearanceMap = {
	pageHistoryContentState: {
		ColorContainerStyles: xcss({
			height: '10px',
			width: '10px',
			marginTop: 'space.0',
			marginBottom: 'space.0',
			marginLeft: 'space.050',
			marginRight: 'space.050',
		}),
		PlainContentStateColorStyles: xcss({
			borderRadius: 'radius.full',
			width: '10px',
			height: '10px',
		}),
		scale: 'scale(0.625)',
		viewbox: '0 0 10px 10px',
	},
	rendererContentState: {
		ColorContainerStyles: xcss({
			height: '12px',
			width: '12px',
			marginTop: 'space.0',
			marginBottom: 'space.0',
			marginRight: 'space.100',
			marginLeft: 'space.100',
		}),
		PlainContentStateColorStyles: xcss({
			borderRadius: 'radius.xlarge',
			width: '12px',
			height: '12px',
		}),
		scale: 'scale(0.75)',
		viewbox: '0 0 12px 12px',
	},
	editorContentState: {
		ColorContainerStyles: xcss({
			height: '16px',
			width: '16px',
			marginTop: 'space.0',
			marginBottom: 'space.0',
			marginLeft: 'space.400',
			marginRight: 'space.400',
		}),
		PlainContentStateColorStyles: xcss({
			borderRadius: 'radius.full',
			width: '16px',
			height: '16px',
		}),
		scale: 'scale(1)',
		viewbox: '0 0 16px 16px',
	},
	contentStateButton: {
		ColorContainerStyles: xcss({
			height: '12px',
			width: '12px',
			marginRight: 'space.050',
		}),
		PlainContentStateColorStyles: xcss({
			borderRadius: 'radius.xlarge',
			width: '12px',
			height: '12px',
		}),
		scale: 'scale(0.75)',
		viewbox: '0 0 12px 12px',
	},
	spaceAdminContentState: {
		ColorContainerStyles: xcss({
			maxHeight: '16px',
			maxWidth: '16px',
			marginRight: 'space.200',
		}),
		PlainContentStateColorStyles: xcss({
			borderRadius: 'radius.full',
			width: '16px',
			height: '16px',
		}),
		scale: 'scale(1)',
		viewbox: '0 0 16px 16px',
	},
	restoreContentStatesModal: {
		ColorContainerStyles: xcss({
			maxHeight: '16px',
			maxWidth: '16px',
		}),
		PlainContentStateColorStyles: xcss({
			borderRadius: 'radius.full',
			width: '16px',
			height: '16px',
		}),
		scale: 'scale(1)',
		viewbox: '0 0 16px 16px',
	},
	subtleContentStateButton: {
		ColorContainerStyles: xcss({
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',

			height: '24px',
			width: '24px',

			marginRight: 'space.negative.025',
		}),
		PlainContentStateColorStyles: xcss({
			borderRadius: 'radius.full',
			width: '10px',
			height: '10px',
		}),
	},
	subtleRendererContentState: {
		ColorContainerStyles: xcss({
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',

			height: '24px',
			width: '24px',
		}),
		PlainContentStateColorStyles: xcss({
			borderRadius: 'radius.full',
			width: '10px',
			height: '10px',
		}),
	},
};

// The SVG defaults to 16x16
// We need to scale to different sizes
const CheckCircleSvg = (color: string, dataTestId?: string, scale?: string, viewbox?: string) => (
	<svg
		width="100%"
		height="100%"
		viewBox={viewbox}
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
		style={{ display: 'block' }}
		data-testid={dataTestId}
	>
		<g transform={scale}>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M9.62505 0.990813C8.94443 -0.330271 7.05558 -0.330271 6.37496 0.990813C5.96598 1.78463 5.04334 2.1668 4.19284 1.89468C2.77742 1.4418 1.4418 2.77742 1.89468 4.19284C2.1668 5.04334 1.78463 5.96598 0.990813 6.37496C-0.330271 7.05558 -0.330271 8.94443 0.990813 9.62505C1.78463 10.034 2.1668 10.9567 1.89468 11.8072C1.4418 13.2226 2.77742 14.5582 4.19284 14.1053C5.04334 13.8332 5.96598 14.2154 6.37496 15.0092C7.05558 16.3303 8.94443 16.3303 9.62505 15.0092C10.034 14.2154 10.9567 13.8332 11.8072 14.1053C13.2226 14.5582 14.5582 13.2226 14.1053 11.8072C13.8332 10.9567 14.2154 10.034 15.0092 9.62505C16.3303 8.94443 16.3303 7.05558 15.0092 6.37496C14.2154 5.96598 13.8332 5.04334 14.1053 4.19284C14.5582 2.77742 13.2226 1.4418 11.8072 1.89468C10.9567 2.1668 10.034 1.78463 9.62505 0.990813ZM11.2176 5.5246L11.2951 5.59368C11.6175 5.91602 11.6405 6.42433 11.3642 6.77323L11.2951 6.85076L7.73956 10.4063C7.41722 10.7287 6.90894 10.7517 6.56003 10.4754L6.48251 10.4063L4.70473 8.62854C4.3576 8.28141 4.3576 7.71859 4.70473 7.37146C5.02707 7.04912 5.53535 7.02609 5.88425 7.30238L5.96178 7.37146L7.11117 8.52029L10.0381 5.59368C10.3604 5.27134 10.8687 5.24831 11.2176 5.5246Z"
				fill={color}
			/>
		</g>
	</svg>
);

const getDisplayedColor = (color: string, isWarning?: boolean): string => {
	if (isWarning) {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		return Boolean(process.env.REACT_SSR) ? R200 : PRIMARY_COLORS.RED;
	}
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	return Boolean(process.env.REACT_SSR) ? color : hexToToken(color);
};

type ContentStateColorProps = {
	color?: string | null;
	restrictionLevel?: ContentStateRestrictionLevel | null;
	appearance: ColorAppearance;
	isWarning?: boolean;
	dataTestId?: string;
};

export const ContentStateColor: FC<ContentStateColorProps> = ({
	color,
	restrictionLevel = 'NONE',
	appearance,
	isWarning = false,
	dataTestId,
}) => {
	const { ColorContainerStyles, viewbox, PlainContentStateColorStyles, scale } =
		ContentStateColorSizes[appearance];
	const displayedColor: string = getDisplayedColor(color ?? B200, isWarning);

	const isRestrictedState = restrictionLevel !== 'NONE' && !isWarning;

	return (
		// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
		<Box xcss={ColorContainerStyles}>
			{isRestrictedState ? (
				CheckCircleSvg(displayedColor, dataTestId, scale, viewbox)
			) : (
				<Box
					testId={dataTestId}
					// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
					xcss={PlainContentStateColorStyles}
					// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
					// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
					style={{ backgroundColor: displayedColor, scale }}
				/>
			)}
		</Box>
	);
};
